import { type DashboardSpec, type PanelSpec } from "../ChartSpec";
import { getLeftAxes } from "./axes";
import { buildAllStreamsPanel, buildCategoryPanel, withAxisId } from "./panel-builders";

/**
 * "single"  — one panel, several left axes and nothing else
 * "stacked" — several panels, each one left axis, every unit category distinct
 * "fixed"   — anything else; the toggle is hidden
 *
 * Only these two shapes round-trip through merge and split. A richer document (say a two-axis
 * panel above per-category panels) would be flattened by merge with no way back, so it is "fixed".
 */
export type PanelComposition = "single" | "stacked" | "fixed";

const isCategoryPanel = (panel: PanelSpec) => panel.axes.length === 1 && getLeftAxes(panel.axes).length === 1;
const allDistinct = <T>(values: T[]) => new Set(values).size === values.length;

export function getPanelComposition(spec: DashboardSpec): PanelComposition {
    const { panels } = spec;

    if (panels.length === 1) {
        const { axes } = panels[0];
        return axes.length > 1 && getLeftAxes(axes).length === axes.length ? "single" : "fixed";
    }

    if (panels.length > 1 && panels.every(isCategoryPanel) && allDistinct(panels.map((p) => p.axes[0].unitCategory))) {
        return "stacked";
    }

    return "fixed";
}

function splitPanel(panel: PanelSpec): PanelSpec[] {
    return panel.axes.map((axis) =>
        buildCategoryPanel(
            axis.unitCategory,
            panel.streams.filter((stream) => stream.axisId === axis.id),
        ),
    );
}

/** One panel per axis. Stream entries are carried over as-is so edits like color survive. Panel-level fields are not. */
export function splitPanelsByCategory(spec: DashboardSpec): DashboardSpec {
    let changed = false;
    const panels = spec.panels.flatMap<PanelSpec>((panel) => {
        if (getLeftAxes(panel.axes).length <= 1) return [panel];
        changed = true;
        return splitPanel(panel);
    });
    return changed ? { ...spec, panels } : spec;
}

/** Every panel into one, with one left axis per unit category. */
export function mergePanels(spec: DashboardSpec): DashboardSpec {
    if (spec.panels.length <= 1) return spec;

    // A stream's axisId is its unit category by convention; look it up through the panel's axes in case it isn't.
    const streams = spec.panels.flatMap((panel) => {
        const categoryOf = new Map(panel.axes.map((axis) => [axis.id, axis.unitCategory]));
        return panel.streams.map((stream) => withAxisId(stream, categoryOf.get(stream.axisId) ?? stream.axisId));
    });

    const merged = buildAllStreamsPanel(streams);
    return { ...spec, panels: merged ? [merged] : [] };
}
